import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Brand palette for the shell (top header, sidebar, dashboard hero, UDA map).
 *
 * The colours themselves live in index.css as --brand-* variables keyed on
 * <html data-palette="…">; this store only remembers the choice and stamps
 * the attribute. 'yellow' (the brown-and-gold DTCP look) is the default.
 */
export type BrandPalette = 'navy' | 'forest' | 'red' | 'orange' | 'saffron' | 'yellow' | 'teal';

export const DEFAULT_BRAND_PALETTE: BrandPalette = 'yellow';

/** Picker order, labels and swatch colours. */
export const BRAND_PALETTES: { key: BrandPalette; label: string; swatch: string }[] = [
  { key: 'navy', label: 'Navy', swatch: '#1F3B73' },
  { key: 'forest', label: 'Forest', swatch: '#22543D' },
  { key: 'red', label: 'Red', swatch: '#D7261E' },
  { key: 'orange', label: 'Orange', swatch: '#D9480F' },
  { key: 'saffron', label: 'Saffron', swatch: '#F7931E' },
  { key: 'yellow', label: 'Yellow', swatch: '#F2BD4A' },
  { key: 'teal', label: 'Teal', swatch: '#137C74' },
];

const applyPalette = (palette: BrandPalette) => {
  if (typeof document !== 'undefined') document.documentElement.dataset.palette = palette;
};

interface BrandPaletteState {
  palette: BrandPalette;
  setPalette: (palette: BrandPalette) => void;
}

export const useBrandPaletteStore = create<BrandPaletteState>()(
  persist(
    (set) => ({
      palette: DEFAULT_BRAND_PALETTE,
      setPalette: (palette) => {
        applyPalette(palette);
        set({ palette });
      },
    }),
    {
      name: 'brand-palette',
      onRehydrateStorage: () => (state) => applyPalette(state?.palette ?? DEFAULT_BRAND_PALETTE),
    }
  )
);

// Stamp the attribute straight away, before anything paints.
applyPalette(useBrandPaletteStore.getState().palette);
